import React from 'react';

interface OptimizedImageProps {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  className?: string;
  priority?: boolean;
  fallbackSrc?: string; // Added fallbackSrc prop
}

export const OptimizedImage: React.FC<OptimizedImageProps> = ({
  src,
  alt,
  width,
  height,
  className,
  priority = false,
  fallbackSrc, // Destructure fallbackSrc
}) => {
  // Add loading attribute based on priority
  const loadingAttr = priority ? 'eager' : 'lazy';
  
  // Add descriptive alt text if none provided
  const safeAlt = alt || 'Image on TreguIm';
  
  // Clean the source URL if it's from our domain
  let currentSrc = src;
  if (typeof src === 'string') {
    // Ensure we have the full URL if it's a relative path
    if (src.startsWith('/')) {
      currentSrc = `${window.location.origin}${src}`;
    }
    
    // If it's our domain, clean up any problematic parameters
    if (currentSrc.includes('treguim.org')) {
      try {
        const urlObj = new URL(currentSrc);
        urlObj.search = ''; // Remove all query parameters for image tags
        currentSrc = urlObj.toString();
      } catch (e) {
        // If URL is invalid, currentSrc remains unchanged
        console.warn('Invalid URL for OptimizedImage:', currentSrc, e);
      }
    }
  }

  const handleError = (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
    if (fallbackSrc && event.currentTarget.src !== fallbackSrc) {
      event.currentTarget.src = fallbackSrc;
    }
  };
  
  return (
    <img
      src={currentSrc}
      alt={safeAlt}
      width={width}
      height={height}
      className={className}
      loading={loadingAttr}
      decoding="async"
      onError={handleError} // Add onError handler
    />
  );
};
